import PostMessageConduit from '../PostMessageConduit';
import { getStyleSheetForBamProduct } from './bamProductStyles';
import { PLAYER_EVENTS, PLAYER_EVENT_PREFIX } from '../constants';

import { BAM_PRODUCT_MODE } from './constants';

const parseMode = (mode = BAM_PRODUCT_MODE.INLINE) => {
  if (Object.values(BAM_PRODUCT_MODE).includes(mode)) return mode;
  return BAM_PRODUCT_MODE.INLINE;
};

const BAM_PRODUCT_EVENTS_PREFIX = 'livecommerce:web-components:bam-product:';
const EVENTS_FOR_IFRAME = {
  PROVIDE_DATA: `${BAM_PRODUCT_EVENTS_PREFIX}provide-data`,
};

const IFRAME_EVENTS_PREFIX = 'livecommerce:react-app:bam-product:';
const EVENTS_FROM_IFRAME = {
  IFRAME_READY: `${IFRAME_EVENTS_PREFIX}iframe-ready`,
};
const PDP_VARIATION_DRAWER_EVENTS = {
  CLOSED: 'livecommerce:pdp-variation-drawer-closed',
  OPENED: 'livecommerce:pdp-variation-drawer-opened',
};

export default class BambuserProductComponent extends window.HTMLElement {
  // Changes in these attributes will trigger attributeChangedCallback()
  static observedAttributes = ['mode', 'playlist'];

  constructor() {
    super();

    this._hasConnected = false;

    this._styleSheet = null;
    this._wrapper = null;
    this._productData = null;
    this._options = null;
    this._onClose = null;
    this._iframe = null;
    this._baseIframeUrl = null;
    this._integrationConfig = null;
    this._theme = null;
    this._event = null;
    this._orgSettings = null;
    this._embedInstance = null;

    this.attachShadow({ mode: 'open' });
  }

  /** Standard Web Component methods */

  connectedCallback() {
    if (this._hasConnected) return;
    this._hasConnected = true;

    this._setup();
    this._setupExternalListeners();

    // Listen for messages from iframe window to contract the player,
    // Without this, when the player iframe is in focus and the parent window can't listen for key events
    // In the player code we have a listener for the Escape key and post a message to the parent window
    this._onWindowMessage = (message, event) => {
      try {
        const { eventName, data = {} } = message;
        const isMessageFromProductIframe = event.source === this._iframe?.contentWindow;

        if (isMessageFromProductIframe && eventName === PDP_VARIATION_DRAWER_EVENTS.OPENED) {
          this.shadowRoot.querySelector('.bam-product-close-button')?.classList.add('is-hidden');
          return;
        }
        if (isMessageFromProductIframe && eventName === PDP_VARIATION_DRAWER_EVENTS.CLOSED) {
          this.shadowRoot.querySelector('.bam-product-close-button')?.classList.remove('is-hidden');
          return;
        }
        if (eventName === 'livecommerce:keyup' && ['Escape', 'Backspace'].includes(data?.key)) {
          this.close();
        }
      } catch (_) {
        // Do nothing
      }
    };
    PostMessageConduit.registerGlobalListener(this._onWindowMessage);
  }

  disconnectedCallback() {
    this._hasConnected = false;
    this._disconnectExternalListeners();
    PostMessageConduit.unregisterGlobalListener(this._onWindowMessage);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this._hasConnected) {
      // Do nothing if the component hasn't been connected to the DOM yet
      return;
    }

    if (oldValue === newValue) return;

    this._setup();
    this._setupExternalListeners();
  }

  /** Custom methods */
  get _attr() {
    const mode = parseMode(this.getAttribute('mode'));
    const playlist = this.getAttribute('playlist');

    return {
      mode,
      playlist,
    };
  }

  _sendMessageToIFrame(message) {
    this._iframe.contentWindow.postMessage(message, this._baseIframeUrl);
  }

  _processMessageFromIFrame = (message) => {
    try {
      const { eventName } = message;
      if (!eventName?.startsWith(IFRAME_EVENTS_PREFIX)) {
        return;
      }

      if (eventName === EVENTS_FROM_IFRAME.IFRAME_READY) {
        this._sendMessageToIFrame(
          {
            eventName: EVENTS_FOR_IFRAME.PROVIDE_DATA,
            data: {
              product: this._productData,
              options: this._options,
              theme: this._theme,
              event: this._event,
              orgSettings: this._orgSettings,
            },
          },
          this._baseIframeUrl,
        );

        // Once we've sent the initial data to the iframe, we can unregister the listener
        PostMessageConduit.unregisterIframeListener(this._iframe, this._processMessageFromIFrame);
      }
    } catch (e) {
      console.log('Error parsing message from player', e);
    }
  };

  _setup() {
    if (!this._productData?.id) return;

    this.shadowRoot.innerHTML = '';
    // Place for setup logic

    const { mode } = this._attr;

    this._styleSheet = this._styleSheet ?? document.createElement('style');

    const newStyle = this._createStyle();

    if (this._styleSheet.textContent !== newStyle) {
      this._styleSheet.textContent = newStyle;
    }

    this._wrapper = this._wrapper ?? document.createElement('div');
    this._wrapper.classList.add('wrapper');

    this._iframe = this._createIframe();

    let closeButton;
    if (this._withCloseButton) {
      closeButton = document.createElement('button');
      closeButton.classList.add('bam-product-close-button');
      closeButton.textContent = '✕';
      closeButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    }

    if (mode === BAM_PRODUCT_MODE.MODAL || mode === BAM_PRODUCT_MODE.DRAWER) {
      const dialog = document.createElement('dialog');
      dialog.classList.add(mode);
      const contentWrapper = document.createElement('div');
      contentWrapper.classList.add('iframe-wrapper');
      dialog.appendChild(contentWrapper);
      contentWrapper.appendChild(this._iframe);
      this._wrapper.appendChild(dialog);
      if (closeButton) dialog.appendChild(closeButton);
    } else {
      const contentWrapper = document.createElement('div');
      contentWrapper.classList.add('iframe-wrapper');
      contentWrapper.appendChild(this._iframe);
      this._wrapper.appendChild(contentWrapper);
      if (closeButton) this._wrapper.appendChild(closeButton);
    }

    const shadowRootChildren = Array.from(this.shadowRoot.children);
    if (!shadowRootChildren.includes(this._styleSheet)) this.shadowRoot.appendChild(this._styleSheet);
    if (!shadowRootChildren.includes(this._wrapper)) this.shadowRoot.appendChild(this._wrapper);

    // Setup postMessage listener for messages coming from the iframe window
    PostMessageConduit.registerIframeListener(this._iframe, this._processMessageFromIFrame);
  }

  _createStyle() {
    const { mode } = this._attr;
    return getStyleSheetForBamProduct({
      mode,
    });
  }

  show(
    {
      productId,
      productData,
      integrationConfig,
      embedInstanceKey,
      playerUrl,
      theme,
      event,
      orgSettings,
      onClose,
      withCloseButton,
      eventHandlers,
    } = {},
    options = {},
  ) {
    if (!productId) {
      throw new Error('Product ID is required');
    }
    if (!this._hasConnected) {
      throw new Error('Component is not connected to the DOM');
    }

    this._productData = productData;
    this._integrationConfig = integrationConfig;
    this._eventHandlers = eventHandlers || [];

    let basePlayerUrl = playerUrl.split('#')[0];
    // Check if we need to rewrite the base URL if the provided playerUrl
    // does not point to an existing Bambuser player build URL. In normal cases
    // this should be taken care of in the Lambda function which should redirect to
    // the appropriate player URL build, but if we'd do the redirect in the cloudfront request
    // we'd still have the now 'outdated' baseURL stored as variable here, and any postMessage
    // where we'd use this 'outdated' targetOrigin would likely fail. Instead we attempt to
    // rewrite the basePlayerUrl to point to the default player build URL without the need of
    // the Lambda function to do any redirection. Would likely be a good idea to refactor this
    // to be done in the Lambda function instead, but for now this should work without too much
    // extra work.
    try {
      const regex = /^https?:\/\/(?:[a-zA-Z0-9-]+\.)?bambuser\.com\/(?!default\/?)([^\/]+)\/?$/;
      const match = basePlayerUrl.match(regex);
      if (basePlayerUrl && match && match[1]) {
        // Construct a new regex to specifically target the captured match[1] at the end
        // This ensures we replace the *intended* segment, not the domain.
        const targetSegmentRegex = new RegExp(`/${match[1]}/?$`);
        basePlayerUrl = basePlayerUrl.replace(targetSegmentRegex, '/default/');
      }
    } catch (error) {
      // Do nothing and hope for the best
    }

    this._baseIframeUrl = basePlayerUrl;
    this._options = options;
    this._theme = theme;
    this._event = event;
    this._orgSettings = orgSettings;
    this._onClose = onClose;
    this._embedInstanceKey = embedInstanceKey;
    this._withCloseButton = withCloseButton;
    this._setup();

    const { mode } = this._attr;

    switch (mode) {
      case BAM_PRODUCT_MODE.INLINE:
        break;
      case BAM_PRODUCT_MODE.DRAWER:
        this._openDrawer();
        break;
      case BAM_PRODUCT_MODE.MODAL:
        this._openModal();
        break;
    }
  }

  _openModal() {
    const dialog = this.shadowRoot.querySelector('dialog');
    const onDialogClick = (event) => {
      if (event.target === dialog) {
        event.stopPropagation();
        dialog.removeEventListener('click', onDialogClick);
        dialog.close();
        this.close();
      }
    };
    dialog?.addEventListener('click', onDialogClick);
    dialog?.showModal();
  }

  _openDrawer() {
    const dialog = this.shadowRoot.querySelector('dialog');
    const onDialogClick = (event) => {
      if (event.target === dialog) {
        event.stopPropagation();
        dialog.classList.add('closing');
        dialog.removeEventListener('click', onDialogClick);
        window.setTimeout(() => {
          dialog.close();
          this.close();
        }, 150);
      }
    };
    dialog?.addEventListener('click', onDialogClick);
    dialog?.showModal();
  }

  _createIframe() {
    const iframe = document.createElement('iframe');

    const url = new URL(`${this._baseIframeUrl}pdp.html`);
    const params = new URLSearchParams({
      productId: this._productData.id,
      locale: this._integrationConfig?.locale,
      currency: this._integrationConfig?.currency,
      embedInstanceKey: this._embedInstanceKey,
      eventId: this._event?.id,
    });

    if (Array.isArray(this._eventHandlers) && this._eventHandlers.length > 0) {
      params.set('eventHandlers', encodeURIComponent(JSON.stringify(this._eventHandlers)));
    }

    iframe.setAttribute('src', `${url}#${params.toString()}`);
    iframe.setAttribute('frameborder', 0);
    iframe.setAttribute('allowTransparency', true);
    iframe.setAttribute('allow', 'fullscreen; autoplay;');
    iframe.setAttribute('title', 'Live video shopping');
    iframe.setAttribute('width', '100%');
    iframe.setAttribute('height', '100%');
    iframe.setAttribute('frameborder', '0');

    return iframe;
  }

  onDisconnectedCallback() {
    this._hasConnected = false;
    this.close();
  }

  close() {
    const productData = this._productData;
    const embedInstance = this._embedInstance;

    this.shadowRoot.replaceChildren();

    this._styleSheet = null;
    this._wrapper = null;
    this._productData = null;
    this._iframe = null;
    this._baseIframeUrl = null;
    this._integrationConfig = null;
    this._eventHandlers = null;
    this._theme = null;
    this._event = null;
    this._orgSettings = null;
    this._embedInstanceKey = null;
    this._embedInstance = null;

    if (productData?.id) {
      const hideProductViewEventName = PLAYER_EVENTS.HIDE_PRODUCT_VIEW.replace(PLAYER_EVENT_PREFIX, '');
      const callbacks = embedInstance?.playerDelegate?.eventSubscribers?.[hideProductViewEventName] || [];
      const { id, productReference: sku, publicUrl: url, title } = productData;
      for (const callback of callbacks) {
        try {
          callback({ id, sku, url, title });
        } catch (err) {
          console.warn('Failed to call hide product view callbacks', { err });
        }
      }
    }

    this._onClose?.();
    this._onClose = null;
  }

  productClickHandler(e) {
    if (e.target.getAttribute('product-action') !== 'none') return;
    this.close();

    if (this._attr.playlist === e?.detail?.containerId || this._attr.playlist === 'auto') {
      this.show(e.detail);
    }
  }

  _setupExternalListeners() {
    const playlists = document.querySelectorAll('bam-playlist[product-action="none"]');
    this._productClickHandler = (...args) => this.productClickHandler(...args);
    playlists.forEach((playlist) => {
      playlist.addEventListener('clickproduct', this._productClickHandler);
    });
  }

  _disconnectExternalListeners() {
    const playlists = document.querySelectorAll('bam-playlist[product-action="none"]');
    playlists.forEach((playlist) => {
      playlist.removeEventListener('clickproduct', this._productClickHandler);
    });
  }

  _connectToEmbedInstance(embedInstance) {
    this._embedInstance = embedInstance;
    // Let the embed instance start listening to messages from this iframe
    embedInstance.connectToIframe(this._iframe, this._processMessageFromIFrame);
  }
}
